// Cutaway pins.
// lg+: tooltip opens on hover via CSS, positioned beside the pin (flip
// classes below choose which side/edge based on available room).
// md and down: tooltip opens on click as a fixed, viewport-centred panel
// (see _main.scss) and stays open until its close button is clicked — a
// pin-anchored popup doesn't reliably fit beside a 26px pin on a small
// screen. Keyboard: Enter/Space toggles .is-open (any size), Escape closes.

const TOOLTIP_WIDTH  = 220;
const TOOLTIP_GAP    = 8 + 6; // caret + spacing
const MOBILE_QUERY   = '(max-width: 991.98px)'; // matches media-breakpoint-down(lg)

function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
}

function classifyPins() {
    document.querySelectorAll('.cutaway-pin').forEach(pin => {
        // Reset all flip classes so we measure from a clean state
        pin.classList.remove('cutaway-pin--flip-left', 'cutaway-pin--flip-top', 'cutaway-pin--flip-bottom');

        const container = pin.closest('.cutaway-img');
        const tooltip    = pin.querySelector('.cutaway-tooltip');
        if (tooltip) {
            tooltip.style.removeProperty('left');
            tooltip.style.removeProperty('right');
        }
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const pinRect       = pin.getBoundingClientRect();
        const tooltipHeight = tooltip ? tooltip.offsetHeight : 80;

        // --- Horizontal check (left/right) ---
        const spaceRight = containerRect.right - (pinRect.right + TOOLTIP_GAP + TOOLTIP_WIDTH);
        const flipLeft   = spaceRight < 0;
        if (flipLeft) pin.classList.add('cutaway-pin--flip-left');

        // --- Vertical check (top/bottom) when showing beside the pin ---
        // The tooltip is vertically centred on the pin; check both edges.
        const tooltipTop    = pinRect.top + (pinRect.height / 2) - (tooltipHeight / 2);
        const tooltipBottom = tooltipTop + tooltipHeight;

        if (tooltipTop < containerRect.top) {
            // Tooltip bleeds above — open below the pin instead
            pin.classList.add('cutaway-pin--flip-bottom');
        } else if (tooltipBottom > containerRect.bottom) {
            // Tooltip bleeds below — open above the pin instead
            pin.classList.add('cutaway-pin--flip-top');
        }

        // --- Final horizontal safety net (lg+ only in practice — md-down
        // overrides position with !important, see _main.scss) ---
        // A pin near a corner can end up with both a horizontal and a
        // vertical flip class at once; the vertical one's left/right values
        // (centring the tooltip) win in the stylesheet since it's declared
        // later, which can silently undo the horizontal flip and let the
        // tooltip run past the left or right edge anyway. Measure where it
        // actually ended up and set an absolute pixel `left` directly on the
        // element — inline styles win over any stylesheet rule regardless of
        // which flip combination applied, so this is a reliable last word on
        // horizontal position without having to duplicate that cascade logic.
        if (tooltip) {
            const rect = tooltip.getBoundingClientRect();
            const pinBoxRect = pin.getBoundingClientRect();
            if (rect.right > containerRect.right) {
                tooltip.style.left  = Math.round((containerRect.right - pinBoxRect.left) - tooltip.offsetWidth) + 'px';
                tooltip.style.right = 'auto';
            } else if (rect.left < containerRect.left) {
                tooltip.style.left  = Math.round(containerRect.left - pinBoxRect.left) + 'px';
                tooltip.style.right = 'auto';
            }
        }
    });
}

// .cutaway-pin has its own `transform` (to centre the dot on its top/left
// point), which makes it a containing block for any position:fixed
// descendant — so the mobile tooltip's `position:fixed; top/left:50%` was
// centring on the 26px pin box, not the viewport. Portal it to <body> while
// open so `position:fixed` actually resolves against the real viewport, and
// put it back where it came from on close.
// Centres the tooltip over the .cutaway-img section rather than the whole
// viewport — the section may sit well below the top of a long page, so
// viewport-centring could land the tooltip over unrelated content above or
// below it. position:fixed's coordinates are viewport-relative, and
// getBoundingClientRect() already reports viewport-relative coordinates too,
// so the section's on-screen centre can be used directly as the fixed
// position. Clamped to stay fully on-screen in case the section is taller
// than the viewport or only partially scrolled into view.
function positionMobileTooltip(pin, tooltip) {
    const section = pin.closest('.cutaway-img');
    if (!section) return;

    const sectionRect = section.getBoundingClientRect();
    const halfW = tooltip.offsetWidth / 2;
    const halfH = tooltip.offsetHeight / 2;
    const margin = 16;

    let centerX = sectionRect.left + sectionRect.width / 2;
    let centerY = sectionRect.top + sectionRect.height / 2;
    centerX = Math.min(Math.max(centerX, halfW + margin), window.innerWidth - halfW - margin);
    centerY = Math.min(Math.max(centerY, halfH + margin), window.innerHeight - halfH - margin);

    tooltip.style.setProperty('--mobile-tooltip-x', centerX + 'px');
    tooltip.style.setProperty('--mobile-tooltip-y', centerY + 'px');
}

function openMobileTooltip(pin, tooltip) {
    if (!tooltip.parentNode || tooltip.parentNode === document.body) return;
    tooltip._homePin = pin;
    tooltip._homeNextSibling = tooltip.nextSibling;
    // classifyPins() may have set an inline left/right for the lg+ overflow
    // correction — an inline style, so it would otherwise win over the
    // mobile centred-panel CSS's left:50%.
    tooltip.style.removeProperty('left');
    tooltip.style.removeProperty('right');
    document.body.appendChild(tooltip);
    pin.classList.add('is-open');
    // The mobile centred-panel CSS targets .cutaway-tooltip.is-open directly
    // (not nested under .cutaway-pin.is-open), since once portal'd the
    // tooltip is a child of <body>, not of the pin.
    tooltip.classList.add('is-open');
    positionMobileTooltip(pin, tooltip);
}

function returnTooltipHome(tooltip) {
    tooltip.classList.remove('is-open');
    if (!tooltip._homePin) return;
    if (tooltip._homeNextSibling) {
        tooltip._homePin.insertBefore(tooltip, tooltip._homeNextSibling);
    } else {
        tooltip._homePin.appendChild(tooltip);
    }
    tooltip._homePin = null;
    tooltip._homeNextSibling = null;
}

// Only one pin's tooltip may be persistently open (click on md-down,
// keyboard Enter/Space at any size) at a time — opening one closes whichever
// other pin was previously open this way. Transient hover-shown tooltips
// (lg+ only) aren't tracked here: a cursor can only be over one pin at once,
// so they're already naturally exclusive without any bookkeeping.
let openPinEl = null;

function initCutawayPins() {
    classifyPins();

    document.querySelectorAll('.cutaway-pin').forEach(pin => {
        const btn      = pin.querySelector('.cutaway-pin__btn');
        const tooltip  = pin.querySelector('.cutaway-tooltip');
        const closeBtn = tooltip?.querySelector('.cutaway-tooltip__close');
        if (!btn || !tooltip) return;

        function close() {
            pin.classList.remove('is-open');
            returnTooltipHome(tooltip);
            if (openPinEl === pin) openPinEl = null;
        }
        // Referenced by other pins (via openPinEl) to close this one when a
        // different pin is opened.
        pin._closeCutawayTooltip = close;

        function open() {
            if (openPinEl && openPinEl !== pin) openPinEl._closeCutawayTooltip?.();
            if (isMobile()) {
                openMobileTooltip(pin, tooltip);
            } else {
                pin.classList.add('is-open');
            }
            openPinEl = pin;
        }

        // md-down: opens on click and stays open regardless of hover/focus
        // until the close button is clicked. lg+: hover already handles
        // opening via CSS, so a click toggle isn't needed there and would
        // change the current hover-only behavior.
        btn.addEventListener('click', () => {
            if (!isMobile()) return;
            if (pin.classList.contains('is-open')) {
                close();
            } else {
                open();
            }
        });

        closeBtn?.addEventListener('click', e => {
            e.preventDefault();
            e.stopPropagation();
            close();
            btn.focus();
        });

        btn.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (pin.classList.contains('is-open')) {
                    close();
                } else {
                    open();
                }
            }
            if (e.key === 'Escape') {
                close();
                btn.blur();
            }
        });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCutawayPins);
} else {
    initCutawayPins();
}

window.addEventListener('resize', () => {
    classifyPins();
    // Edge case: the window was resized past the lg breakpoint while a
    // mobile tooltip was open (portal'd to <body>) — its positioning rules
    // only match while it's a child of .cutaway-pin, so put it back and
    // close it rather than leave it detached and unstyled.
    const portaledTooltips = Array.from(document.body.children)
        .filter(el => el.classList.contains('cutaway-tooltip'));

    if (!isMobile()) {
        portaledTooltips.forEach(tooltip => {
            const pin = tooltip._homePin;
            returnTooltipHome(tooltip);
            pin?.classList.remove('is-open');
            if (openPinEl === pin) openPinEl = null;
        });
    } else {
        // Still mobile (e.g. orientation change) — recentre over the section
        // at its new size/position rather than leaving it where it was.
        portaledTooltips.forEach(tooltip => {
            if (tooltip._homePin) positionMobileTooltip(tooltip._homePin, tooltip);
        });
    }
}, { passive: true });
